import { z } from 'zod';

/**
 * Response envelope and pagination (doc 05 section 3).
 *
 * Every list response has the same shape. Cursors are opaque to the client by
 * contract -- documented as such -- so we can change the encoding without a
 * major version bump.
 */

export const CursorSchema = z.string().min(1).max(512);

export const PaginationSchema = z.object({
  next_cursor: z.string().nullable(),
  has_more: z.boolean(),
  limit: z.number().int(),
  total: z.number().int().optional(),
});
export type Pagination = z.infer<typeof PaginationSchema>;

export function listResponse<T extends z.ZodTypeAny>(item: T) {
  return z.object({ data: z.array(item), pagination: PaginationSchema });
}

/** Default 50, max 200. CDR export raises the ceiling; see doc 05 section 3. */
export const ListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: CursorSchema.optional(),
  include_total: z.coerce.boolean().default(false),
});
export type ListQuery = z.infer<typeof ListQuerySchema>;

/**
 * Money is a STRING everywhere it crosses a boundary.
 *
 * Postgres numeric(18,6) does not fit in a JS number without loss, and the loss
 * is silent. Parsing to a float for display is the specific bug this prevents
 * (doc 07 section 3). Format at the edge, from the string.
 */
export const MoneySchema = z.string().regex(/^-?\d+(\.\d{1,6})?$/);

export const E164Schema = z.string().regex(/^\+[1-9]\d{6,14}$/);
export const SlugSchema = z.string().regex(/^[a-z0-9]([a-z0-9-]{0,60}[a-z0-9])?$/);
export const SipDomainSchema = z.string().regex(/^[a-z0-9]([a-z0-9.-]{0,251}[a-z0-9])?$/);

/** ISO 8601 with an explicit offset. Never a bare local timestamp. */
export const TimestampSchema = z.string().datetime({ offset: true });

export const IdSchema = z.string().uuid();

/** Sort spec: comma list, '-' prefix for descending. */
export const SortSchema = z.string().regex(/^-?[a-z_]+(,-?[a-z_]+)*$/);
